<template>
	<el-dialog
		title="批量导入商品图片"
		top="60px"
		class="dialog-title batch-import-img-dialog"
		:visible.sync="dialogVisible"
		width="900px"
		:close-on-click-modal="false"
		:before-close="handleClose"
		append-to-body>
		<div class="batch-import-img-body">
			<div class="upload-action-row">
				<el-button type="primary" size="small" :disabled="isTotalLimitReached" @click="triggerUpload">上传图片</el-button>
				<span class="upload-tip">注意：单张图片最大{{ maxSize }}M，单次最多可选{{ maxSelectCount }}张图片，每次导入最多{{ maxTotalCount }}张。</span>
			</div>
			<div class="img-list-box">
				<div
					v-for="(item, index) in imageList"
					:key="item.id"
					class="img-item"
					@mouseover="hoverIndex = index"
					@mouseout="hoverIndex = -1">
					<div class="img-preview-wrap">
						<img :src="item.url" class="img-preview" alt="">
						<div
							v-if="item.status !== 'success'"
							class="img-status-mask"
							:class="item.status">
							<i v-if="item.status === 'uploading'" class="el-icon-loading"></i>
							<span>{{ statusText(item.status) }}</span>
						</div>
						<div class="img-btn" v-show="hoverIndex === index && item.status !== 'uploading'">
							<el-button class="upload-btn" type="primary" @click="reupload(index)"><i class="el-icon-upload"></i></el-button>
							<el-button class="delete-btn" type="danger" @click="removeImage(index)"><i class="el-icon-delete"></i></el-button>
							<el-button class="search-btn" type="danger" @click="previewImage(item.url)"><i class="el-icon-search"></i></el-button>
						</div>
					</div>
					<div class="img-name" :title="item.name">{{ item.name }}</div>
				</div>
			</div>
			<div class="title-btn-box">
				<el-button class="btn" plain type="primary" size="small" @click="handleClose">取消</el-button>
				<el-button
					class="btn"
					plain
					type="primary"
					size="small"
					:loading="importing"
					:disabled="isImageUploading"
					@click="confirmImport">确认导入</el-button>
			</div>
		</div>
		<input
			type="file"
			ref="fileInput"
			accept="image/png,image/jpeg,image/jpg,image/gif"
			multiple
			v-show="false"
			@change="handleFileChange">
		<input
			type="file"
			ref="reuploadInput"
			accept="image/png,image/jpeg,image/jpg,image/gif"
			v-show="false"
			@change="handleReuploadChange">
		<el-dialog
			title="图片预览"
			class="dialog-title"
			:visible.sync="previewVisible"
			width="500px"
			append-to-body
			:close-on-click-modal="false">
			<div style="text-align: center;">
				<img :src="previewUrl" style="max-width: 100%; max-height: 400px;">
			</div>
		</el-dialog>
	</el-dialog>
</template>

<script>
export default {
	name: 'BatchImportImg',
	props: {
		visible: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			imageList: [],
			hoverIndex: -1,
			reuploadIndex: -1,
			importing: false,
			previewVisible: false,
			previewUrl: '',
			uploadType: new RegExp(/image\/png|image\/jpg|image\/jpeg|image\/gif/),
			maxSize: 5,
			maxSelectCount: 10,
			maxTotalCount: 200,
			isQueueRunning: false
		}
	},
	computed: {
		dialogVisible: {
			get () {
				return this.visible
			},
			set (val) {
				this.$emit('update:visible', val)
			}
		},
		isImageUploading () {
			return this.isQueueRunning || this.imageList.some(item => item.status === 'uploading' || item.status === 'pending')
		},
		isTotalLimitReached () {
			return this.imageList.length >= this.maxTotalCount
		}
	},
	beforeDestroy () {
		this.revokeAllUrls()
	},
	methods: {
		statusText (status) {
			const map = {
				pending: '待上传',
				uploading: '正在上传',
				error: '上传失败'
			}
			return map[status] || ''
		},
		triggerUpload () {
			if (this.isTotalLimitReached) {
				this.$message.warning(`每次导入最多${this.maxTotalCount}张，超出部分已被自动放弃`)
				return
			}
			this.$refs.fileInput.click()
		},
		reupload (index) {
			this.reuploadIndex = index
			this.$refs.reuploadInput.click()
		},
		handleFileChange (e) {
			this.addFiles(Array.from(e.target.files || []))
			e.target.value = ''
		},
		handleReuploadChange (e) {
			const files = e.target.files
			if (!files || !files.length || this.reuploadIndex < 0) {
				e.target.value = ''
				return
			}
			const file = files[0]
			const error = this.validateFile(file)
			if (error) {
				this.$message.error(error)
				e.target.value = ''
				return
			}
			const oldItem = this.imageList[this.reuploadIndex]
			this.revokeItemUrl(oldItem)
			const newItem = this.createImageItem(file)
			this.$set(this.imageList, this.reuploadIndex, newItem)
			this.reuploadIndex = -1
			e.target.value = ''
			this.processUploadQueue()
		},
		addFiles (files) {
			if (!files.length) {
				return
			}
			const totalRemain = this.maxTotalCount - this.imageList.length
			if (totalRemain <= 0) {
				this.$message.warning(`每次导入最多${this.maxTotalCount}张，超出部分已被自动放弃`)
				return
			}
			const addLimit = Math.min(this.maxSelectCount, totalRemain)
			const willTruncateByBatch = files.length > this.maxSelectCount
			const willTruncateByTotal = files.length > totalRemain
			if (willTruncateByBatch) {
				this.$message.warning(`单次最多可选${this.maxSelectCount}张，超出部分已被自动放弃`)
			}
			if (willTruncateByTotal && (!willTruncateByBatch || totalRemain < this.maxSelectCount)) {
				this.$message.warning(`每次导入最多${this.maxTotalCount}张，超出部分已被自动放弃`)
			}
			const newItems = []
			let addedCount = 0
			for (let i = 0; i < files.length; i++) {
				if (addedCount >= addLimit) {
					break
				}
				const file = files[i]
				const error = this.validateFile(file)
				if (error) {
					this.$message.error(`${file.name}：${error}`)
					continue
				}
				newItems.push(this.createImageItem(file))
				addedCount++
			}
			if (newItems.length) {
				this.imageList.push(...newItems)
				this.processUploadQueue()
			}
		},
		validateFile (file) {
			if (!this.uploadType.test(file.type)) {
				return '上传图片只支持png、jpg、jpeg、gif格式'
			}
			if (file.size / 1024 / 1024 > this.maxSize) {
				return `上传图片大小不能超过${this.maxSize}M`
			}
			return ''
		},
		createImageItem (file) {
			return {
				id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
				name: '',
				url: URL.createObjectURL(file),
				file,
				tempUrl: '',
				status: 'pending'
			}
		},
		async processUploadQueue () {
			if (this.isQueueRunning) {
				return
			}
			this.isQueueRunning = true
			try {
				for (const item of this.imageList) {
					if (item.status === 'pending') {
						await this.uploadSingle(item)
					}
				}
			} finally {
				this.isQueueRunning = false
			}
		},
		uploadSingle (item) {
			item.status = 'uploading'
			const formData = new FormData()
			formData.append('extension', item.file.type.split('/')[1])
			formData.append('file', item.file)
			return this.$store.dispatch('requestFilePost', {
				url: `${this.$fxBaseApi.urlQIVShop}/batch/upload/pic/tempcompression`,
				data: formData
			}).then(res => {
				if (res.result) {
					item.tempUrl = res.data.url
					item.name = res.data.name
					item.status = 'success'
				} else {
					item.status = 'error'
					this.$message.error(`${item.name}：${res.msg || '上传图片失败'}`)
				}
			}).catch(() => {
				item.status = 'error'
			})
		},
		removeImage (index) {
			const item = this.imageList[index]
			this.revokeItemUrl(item)
			this.imageList.splice(index, 1)
		},
		previewImage (url) {
			this.previewUrl = url
			this.previewVisible = true
		},
		confirmImport () {
			if (!this.imageList.length) {
				this.$message.error('请先上传图片')
				return
			}
			if (this.isImageUploading) {
				this.$message.warning('图片正在上传中，请稍候')
				return
			}
			const errorItem = this.imageList.find(item => item.status === 'error')
			if (errorItem) {
				this.$message.error('存在上传失败的图片，请重新上传或删除')
				return
			}
			const images = this.imageList.map(item => ({
				name: item.name,
				url: item.tempUrl
			}))
			this.importing = true
			this.$store.dispatch('requestPost', {
				url: `${this.$fxBaseApi.urlReport}/shop/product/showcase/batchBind`,
				data: { images }
			}).then(res => {
				if (res.result) {
					this.$message.success(res.msg)
					this.$emit('success')
					this.resetAndClose()
				} else {
					this.$message.error(res.msg)
				}
			}).catch(() => {
				this.$message.error('导入失败')
			}).finally(() => {
				this.importing = false
			})
		},
		handleClose (done) {
			this.revokeAllUrls()
			this.imageList = []
			this.hoverIndex = -1
			this.reuploadIndex = -1
			this.previewVisible = false
			this.previewUrl = ''
			this.isQueueRunning = false
			this.dialogVisible = false
			done && done()
		},
		resetAndClose () {
			this.handleClose()
		},
		revokeItemUrl (item) {
			if (item && item.url && item.url.startsWith('blob:')) {
				URL.revokeObjectURL(item.url)
			}
		},
		revokeAllUrls () {
			this.imageList.forEach(item => {
				this.revokeItemUrl(item)
			})
		}
	}
}
</script>

<style lang="stylus" scoped>
.batch-import-img-body {
	text-align: left;
	.upload-action-row {
		display: flex;
		align-items: center;
		margin-bottom: 16px;
		.upload-tip {
			margin-left: 12px;
			color: #F56C6C;
			font-size: 12px;
		}
	}
	.img-list-box {
		height: 400px;
		overflow-y: auto;
		border: 1px solid #DCDFE6;
		border-radius: 4px;
		padding: 16px;
		display: flex;
		flex-wrap: wrap;
		align-content: flex-start;
		.img-item {
			width: 100px;
			margin: 0 20px 16px 0;
			.img-preview-wrap {
				position: relative;
				width: 80px;
				height: 80px;
				border-radius: 6px;
				overflow: hidden;
				.img-preview {
					width: 100%;
					height: 100%;
					object-fit: cover;
				}
				.img-status-mask {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					background-color: rgba(0, 0, 0, 0.55);
					color: #fff;
					font-size: 12px;
					line-height: 1.4;
					text-align: center;
					padding: 4px;
					box-sizing: border-box;
					.el-icon-loading {
						font-size: 16px;
						margin-bottom: 4px;
					}
					&.error {
						background-color: rgba(245, 108, 108, 0.75);
					}
				}
				.img-btn {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					background-color: rgba(0, 0, 0, 0.1);
					.el-button {
						padding: 4px;
						position: absolute;
					}
					.upload-btn {
						top: 14px;
						left: 10px;
					}
					.delete-btn {
						top: 14px;
						right: 10px;
					}
					.search-btn {
						bottom: 8px;
						left: 18px;
					}
				}
			}
			.img-name {
				width: 80px;
				margin-top: 6px;
				font-size: 12px;
				color: #606266;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}
		}
	}
	.title-btn-box {
		height: 40px;
		padding: 10px;
		box-sizing: border-box;
		text-align: center;
		margin-top: 10px;
		.btn {
			width: 130px;
		}
	}
}
</style>
